import React, { useState, useEffect } from "react";
import { BookOpen } from "lucide-react";

const ReasonstoJoin = () => {
  const API_BASE_URL = "https://backend.dlapakistan.com/api/member/";
  const emerald600 = "#059669";
  
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load data from API
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}reasons-to-join/`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.status}`);
      }

      const data = await response.json();
      
      // API returns array, so get first item
      if (data && data.length > 0) {
        setContent(data[0]);
      } else {
        setContent(null); // No content available
      }
    } catch (err) {
      console.error("Error loading reasons to join:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col mt-20 min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-600"></div>
        <p className="mt-4 text-gray-600">Loading reasons to join...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col mt-20 min-h-screen items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Unable to Load Content</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadData}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col mt-20 min-h-screen w-full bg-gray-50">
      {/* Header Section */}
      <div className="px-8 py-6 bg-white border-b">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div className="text-left">
            <h1 className="text-3xl font-bold text-gray-900">Reasons To Join DLA</h1>
            <p className="text-gray-600 mt-1">Discover why you should join our community</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm font-medium">
              Read Only
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-8 md:px-12 mt-8 w-full pb-20">
        {!content ? (
          <div className="text-center py-24 bg-white border-2 border-dashed border-gray-300 rounded-3xl">
            <BookOpen className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Content Available</h3>
            <p className="text-gray-500 mb-6">Reasons to join content has not been added yet.</p>
            <div className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-600 rounded-lg">
              <span className="text-sm">Contact administrator to add content</span>
            </div>
          </div>
        ) : (
          <div className="animate-fadeIn">
            {/* Hero Section */}
            <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
              <div className="mb-10 border-l-4 pl-6" style={{ borderColor: emerald600 }}>
                <h2 className="text-4xl font-bold text-gray-900 tracking-tight">
                  {content.title}
                </h2>
                <div className="flex items-center gap-4 mt-4 text-gray-500 text-sm">
                  <div className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
                    <span>Last updated: {new Date(content.updated_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-blue-500"></span>
                    <span>Active content</span>
                  </div>
                </div>
              </div>

              {/* Content Display */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <div 
                  className="text-gray-700 leading-relaxed text-lg space-y-6 custom-content"
                  dangerouslySetInnerHTML={{ __html: content.description }} 
                />
              </div>
            </div>

            {/* Info Card */}
         
          </div>
        )}
      </div>
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.6s ease-out forwards;
        }
        
        .custom-content h1,
        .custom-content h2,
        .custom-content h3,
        .custom-content h4 {
          color: #111827;
          font-weight: 600;
          margin-top: 1.5em;
          margin-bottom: 0.5em;
        }
        
        .custom-content p {
          margin-bottom: 1em;
          line-height: 1.8;
        }
        
        .custom-content ul,
        .custom-content ol {
          margin-left: 1.5em;
          margin-bottom: 1em;
        }
        
        .custom-content li {
          margin-bottom: 0.5em;
        }
        
        .custom-content a {
          color: #059669;
          text-decoration: underline;
        }
        
        .custom-content blockquote {
          border-left: 4px solid #059669;
          padding-left: 1em;
          margin: 1em 0;
          font-style: italic;
          color: #6b7280;
        }
        
        .custom-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 1em 0;
        }
        
        .custom-content th,
        .custom-content td {
          border: 1px solid #e5e7eb;
          padding: 0.5em 0.75em;
          text-align: left;
        }
        
        .custom-content th {
          background-color: #f9fafb;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
};

export default ReasonstoJoin;